import { Link } from "react-router-dom";
import { useEffect } from "react";
import {
  BookOpen,
  UploadSimple,
  Sliders,
  DownloadSimple,
  Circle,
  PaintBrush,
  Palette,
  Rocket,
  Gauge,
  Medal,
  Eraser,
  Scissors,
  Broom,
  GridFour,
  FloppyDisk,
  Stack,
  CheckCircle,
  Warning,
  ArrowRight,
  Lightning,
} from "@phosphor-icons/react";

export default function HowTo() {
  useEffect(() => {
    const hash = window.location.hash.replace("#", "");
    if (!hash) return;
    // Wait a tick so anchors are mounted before scrolling.
    const t = setTimeout(() => {
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="max-w-[1440px] mx-auto" data-testid="how-to-page">
      {/* Hero */}
      <section className="border-b border-[var(--border)]">
        <div className="px-6 md:px-10 py-14 md:py-20 grid lg:grid-cols-[1.3fr_1fr] gap-10 items-end">
          <div>
            <div className="micro-label flex items-center gap-2">
              <BookOpen size={12} weight="bold" /> / How to use Decal Labs
            </div>
            <h1 className="font-display font-black tracking-tight leading-[0.95] text-5xl md:text-6xl lg:text-7xl mt-3">
              From PNG to
              <br />
              livery-editor
              <br />
              <span className="text-[var(--accent)]">in three clicks.</span>
            </h1>
            <p className="mt-8 text-[var(--fg-2)] max-w-xl">
              Every control, every mode, every shortcut explained. Bookmark this
              page and share it with your team — no racing-vector knowledge required.
            </p>
          </div>
          <nav aria-label="Contents" className="hairline bg-white">
            <div className="micro-label p-4 border-b border-[var(--border)]">
              Jump to
            </div>
            <ul className="divide-y divide-[var(--border)]">
              {[
                ["good-vs-bad", "What works vs what doesn't"],
                ["quick-start", "Quick start (3 steps)"],
                ["modes", "Conversion modes"],
                ["quality", "Quality presets"],
                ["controls", "Sliders & controls"],
                ["background", "Background tools"],
                ["splitter", "Split into pieces"],
                ["repair", "SVG repair"],
                ["save", "Save, publish, share"],
                ["tips", "Pro tips for livery import"],
                ["trouble", "Troubleshooting"],
              ].map(([id, label]) => (
                <li key={id}>
                  <a
                    href={`#${id}`}
                    className="flex items-center justify-between p-4 hover:bg-[var(--bg-2)] text-sm"
                    data-testid={`toc-${id}`}
                  >
                    <span>{label}</span>
                    <ArrowRight size={12} weight="bold" className="text-[var(--fg-2)]" />
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </section>

      {/* What works vs what doesn't — visible at the very top */}
      <Anchor id="good-vs-bad">
        <Section
          label="/ Before you upload"
          title="What works vs what doesn't"
          intro={
            <>
              The 15&nbsp;KB livery cap means simple, bold artwork converts
              beautifully — and yes, you can use images with{" "}
              <span className="font-semibold text-[var(--fg)]">
                multiple colours
              </span>{" "}
              too, just keep them{" "}
              <span className="font-semibold text-[var(--fg)]">
                not too detailed
              </span>
              . Heavily detailed illustrations get stripped down by the
              auto-rescue. Use these two examples as your reality check
              before you spend credits.
            </>
          }
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[var(--border)] border border-[var(--border)]">
            {/* Good example */}
            <div
              className="bg-white p-6 md:p-8"
              data-testid="how-to-good-example"
            >
              <div className="flex items-center justify-between mb-4">
                <span
                  className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full"
                  style={{
                    background: "rgba(16, 185, 129, 0.12)",
                    color: "#047857",
                    border: "1px solid rgba(16, 185, 129, 0.35)",
                  }}
                >
                  <CheckCircle size={14} weight="fill" /> Perfect for 15 KB
                </span>
                <span className="micro-label">Example 01</span>
              </div>
              <div
                className="w-full aspect-square flex items-center justify-center rounded-md overflow-hidden mb-5"
                style={{
                  background:
                    "repeating-conic-gradient(#e9e9e9 0% 25%, #fff 0% 50%) 0 0 / 18px 18px",
                }}
              >
                <img
                  src="https://customer-assets.emergentagent.com/job_svg-racer-studio/artifacts/0m2hhpoq_ChatGPT%20Image%20May%2020%2C%202026%2C%2008_51_24%20PM.png"
                  alt="Flat two-tone navy and gold Sample crown logo — perfect for 15KB conversion"
                  className="max-w-full max-h-full object-contain"
                  loading="lazy"
                />
              </div>
              <h3 className="font-display font-black text-xl mb-2">
                Flat shapes, 1–3 solid colours, clean outlines
              </h3>
              <ul className="text-sm text-[var(--fg-2)] space-y-2">
                <li className="flex gap-2">
                  <CheckCircle
                    size={14}
                    weight="fill"
                    className="text-[var(--success)] flex-shrink-0 mt-0.5"
                  />
                  Solid fills — no gradients, no metallic sheen
                </li>
                <li className="flex gap-2">
                  <CheckCircle
                    size={14}
                    weight="fill"
                    className="text-[var(--success)] flex-shrink-0 mt-0.5"
                  />
                  Bold script and chunky shapes survive the trace
                </li>
                <li className="flex gap-2">
                  <CheckCircle
                    size={14}
                    weight="fill"
                    className="text-[var(--success)] flex-shrink-0 mt-0.5"
                  />
                  Limited palette — 2 to 3 distinct colours
                </li>
                <li className="flex gap-2">
                  <CheckCircle
                    size={14}
                    weight="fill"
                    className="text-[var(--success)] flex-shrink-0 mt-0.5"
                  />
                  Imports as a crisp livery decal with vivid edges
                </li>
              </ul>
            </div>

            {/* Bad example */}
            <div
              className="bg-white p-6 md:p-8"
              data-testid="how-to-bad-example"
            >
              <div className="flex items-center justify-between mb-4">
                <span
                  className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full"
                  style={{
                    background: "rgba(239, 68, 68, 0.10)",
                    color: "#B91C1C",
                    border: "1px solid rgba(239, 68, 68, 0.35)",
                  }}
                >
                  <Warning size={14} weight="fill" /> Too detailed for 15 KB
                </span>
                <span className="micro-label">Example 02</span>
              </div>
              <div
                className="w-full aspect-square flex items-center justify-center rounded-md overflow-hidden mb-5"
                style={{
                  background:
                    "repeating-conic-gradient(#e9e9e9 0% 25%, #fff 0% 50%) 0 0 / 18px 18px",
                }}
              >
                <img
                  src="https://customer-assets.emergentagent.com/job_svg-racer-studio/artifacts/spkdyyt3_ChatGPT%20Image%20May%2020%2C%202026%2C%2008_51_48%20PM.png"
                  alt="Highly detailed Sample crown logo with gradients, gold filigree, gems and ornate ribbons — too detailed for 15KB"
                  className="max-w-full max-h-full object-contain"
                  loading="lazy"
                />
              </div>
              <h3 className="font-display font-black text-xl mb-2">
                Gradients, metallic sheen, ornate filigree
              </h3>
              <ul className="text-sm text-[var(--fg-2)] space-y-2">
                <li className="flex gap-2">
                  <Warning
                    size={14}
                    weight="fill"
                    className="text-[#B91C1C] flex-shrink-0 mt-0.5"
                  />
                  Smooth colour gradients flatten into ugly banded patches
                </li>
                <li className="flex gap-2">
                  <Warning
                    size={14}
                    weight="fill"
                    className="text-[#B91C1C] flex-shrink-0 mt-0.5"
                  />
                  Thousands of tiny filigree curves blow the 15 KB cap
                </li>
                <li className="flex gap-2">
                  <Warning
                    size={14}
                    weight="fill"
                    className="text-[#B91C1C] flex-shrink-0 mt-0.5"
                  />
                  Auto-rescue downsizes and simplifies — losing scrollwork,
                  gem facets, ribbon highlights
                </li>
                <li className="flex gap-2">
                  <Warning
                    size={14}
                    weight="fill"
                    className="text-[#B91C1C] flex-shrink-0 mt-0.5"
                  />
                  Looks muddy or "melted" once imported as a livery decal
                </li>
                <li className="flex gap-2">
                  <Lightning
                    size={14}
                    weight="fill"
                    className="text-[var(--accent)] flex-shrink-0 mt-0.5"
                  />
                  <span>
                    Pro tip: flatten gradients in your editor first, OR
                    enable{" "}
                    <span className="font-semibold">Perfect Vector</span>{" "}
                    (Pro plan) to bypass the 15 KB cap.
                  </span>
                </li>
              </ul>
            </div>
          </div>

          <p className="mt-6 text-sm text-[var(--fg-2)] text-center">
            Not sure which side of the line your artwork sits on?{" "}
            <Link
              to="/convert"
              className="underline font-semibold text-[var(--fg)]"
              data-testid="good-vs-bad-try-convert-link"
            >
              Try a free conversion
            </Link>{" "}
            — the result panel tells you instantly.
          </p>
        </Section>
      </Anchor>

      {/* Quick Start */}
      <Anchor id="quick-start">
        <Section
          label="/ Quick start"
          title="Three clicks to a livery-ready SVG"
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[var(--border)] border border-[var(--border)]">
            <Step
              n="01"
              icon={<UploadSimple size={20} weight="bold" />}
              title="Upload"
            >
              Open <Link to="/convert" className="underline">Convert</Link>, drop
              any PNG, JPG, or transparent image into the dropzone (or click to
              pick). Max 15 MB.
            </Step>
            <Step
              n="02"
              icon={<Sliders size={20} weight="bold" />}
              title="Tune"
            >
              Pick a Mode (B/W, Single, or Multi), choose a Quality preset, and
              tweak sliders. Every change re-traces automatically.
            </Step>
            <Step
              n="03"
              icon={<DownloadSimple size={20} weight="bold" />}
              title="Download"
            >
              When the compatibility chip reads Livery Ready, click{" "}
              <span className="chip">DOWNLOAD SVG</span>. Import into the
              livery editor.
            </Step>
          </div>
        </Section>
      </Anchor>

      {/* Modes */}
      <Anchor id="modes">
        <Section label="/ Conversion modes" title="Pick the right mode for your art">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[var(--border)] border border-[var(--border)]">
            <ModeCard
              icon={<Circle size={20} weight="bold" />}
              name="B/W"
              caption="Single-color, high-contrast logos"
            >
              <p>
                Threshold the image to pure black on transparent. Best for
                line-art, sponsor logos, text, and stencil-style decals.
              </p>
              <p className="mt-2">
                Use the <strong>Threshold</strong> slider to set where "dark" ends
                and "light" begins. Lower = only very dark pixels become ink.
              </p>
            </ModeCard>
            <ModeCard
              icon={<PaintBrush size={20} weight="bold" />}
              name="Single"
              caption="One custom color"
            >
              <p>
                Same as B/W but paints the traced shape in any color you pick
                (hex or picker). Ideal for team colors, brand-matched stripes, or
                mono-color iconography.
              </p>
            </ModeCard>
            <ModeCard
              icon={<Palette size={20} weight="bold" />}
              name="Multi"
              caption="2–8 color quantization"
            >
              <p>
                Groups pixels into N vivid colors using saturation-aware
                K-means, then traces each color as a separate layer. Best for
                multi-color logos, flags, and team graphics.
              </p>
              <p className="mt-2 text-xs">
                More colors = more paths = bigger file. Start with 4, bump only
                when you need it.
              </p>
            </ModeCard>
          </div>
        </Section>
      </Anchor>

      {/* Quality */}
      <Anchor id="quality">
        <Section
          label="/ Quality presets"
          title="Pick the trade-off between fidelity and file size"
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[var(--border)] border border-[var(--border)]">
            <QualityCard
              icon={<Rocket size={20} weight="bold" />}
              name="Fast"
              meta="512 px · coarse"
            >
              Lowest internal trace resolution, tightest file. Use for very
              simple shapes when you need maximum headroom under 15 KB.
            </QualityCard>
            <QualityCard
              icon={<Gauge size={20} weight="bold" />}
              name="Balanced"
              meta="1024 px · default"
            >
              The sweet spot we tuned for the livery editor. Preserves curve fidelity while
              staying well inside the 15 KB ceiling for most single-logo decals.
            </QualityCard>
            <QualityCard
              icon={<Medal size={20} weight="bold" />}
              name="High"
              meta="1536 px · crisp"
            >
              Highest internal resolution — sharpest in-game curves. If the file
              exceeds 15 KB you'll see the fit chip turn red; split the decal
              into pieces to keep the quality.
            </QualityCard>
          </div>
          <Note tone="info">
            We automatically re-compress (bumping Simplification) if your pick
            would exceed 15 KB — quality is only reduced as a last resort.
          </Note>
        </Section>
      </Anchor>

      {/* Controls */}
      <Anchor id="controls">
        <Section label="/ Sliders & controls" title="Exactly what each knob does">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[var(--border)] border border-[var(--border)]">
            <Control label="Threshold" range="1 – 254">
              Where "dark" ends and "light" begins for B/W and Single modes.
              Lower values capture only very dark pixels; higher values include
              mid-tones. Watch the output preview — you'll see more ink appear
              as you slide right.
            </Control>
            <Control label="Colors" range="2 – 8">
              Number of palette entries in Multi mode. More colors = better
              fidelity for complex logos but more traced paths (each path eats
              into the 15 KB budget).
            </Control>
            <Control label="Simplification" range="0.1 – 4.0">
              How aggressively potrace smooths curves. Higher = smoother curves,
              fewer points, smaller file — but slight corner rounding. Default
              0.8 is livery-tuned.
            </Control>
            <Control label="Precision" range="0 – 3 digits">
              Decimal digits kept in every path coordinate. 0 = whole numbers
              only (tiny file, visible stepping on giant decals); 2–3 = smooth
              but larger. 1 is the sweet spot.
            </Control>
          </div>
        </Section>
      </Anchor>

      {/* Background */}
      <Anchor id="background">
        <Section
          label="/ Background tools"
          title="Transparent backgrounds — two paths"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[var(--border)] border border-[var(--border)]">
            <FeatureCard
              icon={<Eraser size={20} weight="fill" />}
              title="Make background transparent (button)"
            >
              One-click preprocessing: the current image is replaced with a
              transparent-bg version, then the tracer runs again. Uses a smart
              flood-fill from the image border — interior pixels matching the
              background colour are preserved.
            </FeatureCard>
            <FeatureCard
              icon={<Lightning size={20} weight="fill" />}
              title="Auto-detect on every trace (always on)"
            >
              Behind the scenes, if your image has a near-uniform border colour
              (white/gray/any solid), we auto-crop to the decal content and
              treat the background as transparent during tracing. No setting
              required — this is why a flat-white-bg JPEG just works.
            </FeatureCard>
          </div>
          <Note tone="info">
            If your source has a busy or photographic border, auto-detect backs
            off and traces the full image as-is. In that case, click{" "}
            <strong>Make background transparent</strong> to force-strip, or
            remove the BG in your image editor first.
          </Note>
        </Section>
      </Anchor>

      {/* Splitter */}
      <Anchor id="splitter">
        <Section
          label="/ Split into pieces"
          title="Dense logo? Divide and conquer."
        >
          <div className="grid grid-cols-1 md:grid-cols-[1.4fr_1fr] gap-px bg-[var(--border)] border border-[var(--border)]">
            <div className="bg-white p-6 md:p-8 space-y-4">
              <div className="flex items-center gap-2 micro-label">
                <GridFour size={14} weight="bold" /> POST-CONVERT SPLITTER
              </div>
              <p>
                Under the Compatibility Panel, set Rows × Cols (up to 4×4) and
                hit <span className="chip">GENERATE PIECES</span>. Every tile is
                converted <em>separately</em>, so each gets its own 15 KB
                budget.
              </p>
              <p>
                A colour-coded Fit chip + bar on every tile shows how close it
                is to the ceiling (green = plenty of room, red = over). Click
                DL on any tile or{" "}
                <span className="chip">DOWNLOAD ALL PIECES</span> to grab them
                sequentially.
              </p>
              <p className="text-sm text-[var(--fg-2)]">
                In-game, position the tiles in the livery editor in the same
                grid — they re-assemble into one large, crisp decal.
              </p>
            </div>
            <div className="bg-white p-6 md:p-8 space-y-4">
              <div className="flex items-center gap-2 micro-label">
                <Scissors size={14} weight="bold" /> STANDALONE SPLITTER
              </div>
              <p>
                The <Link to="/split" className="underline">Split</Link> page
                splits a raw image into PNG tiles (no vectorization). Use it
                when you want to pre-slice artwork and then run each tile
                through Convert yourself — gives maximum manual control.
              </p>
            </div>
          </div>
          <Note tone="success">
            When your result chip reads <strong>OVER</strong>, a "Jump to Split
            Pieces" button appears right below the Compatibility Panel — clicking
            scrolls you straight to the splitter.
          </Note>
        </Section>
      </Anchor>

      {/* Repair */}
      <Anchor id="repair">
        <Section
          label="/ SVG repair"
          title="Paste any SVG, get a livery-safe one back"
        >
          <div className="bg-white hairline p-6 md:p-8 space-y-4">
            <p className="flex items-start gap-3">
              <Broom size={20} weight="bold" className="text-[var(--accent)] mt-1 shrink-0" />
              <span>
                The <Link to="/repair" className="underline">Repair</Link> page
                accepts pasted or uploaded SVGs (from Illustrator, Figma, etc.)
                and strips every element the livery editor can't render: <code>filter</code>,
                {" "}<code>mask</code>, <code>text</code>, <code>image</code>,
                {" "}<code>gradient</code>, <code>script</code>, inline styles,
                metadata. You get a clean, validated SVG back plus a full
                diagnostics list.
              </span>
            </p>
          </div>
        </Section>
      </Anchor>

      {/* Save & Publish */}
      <Anchor id="save">
        <Section label="/ Save, publish, share" title="Keep your work">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[var(--border)] border border-[var(--border)]">
            <FeatureCard icon={<Stack size={20} weight="fill" />} title="Publish to Showcase">
              Click <span className="chip">PUBLISH</span> on Convert. Your decal
              appears on the community
              {" "}<Link to="/showcase" className="underline">Decal Showcase</Link>.
              Only livery-compatible decals can be published — protects gallery
              quality.
            </FeatureCard>
            <FeatureCard icon={<DownloadSimple size={20} weight="fill" />} title="Discover others">
              Browse the Showcase, sort by Newest or Most Downloaded, and grab
              anything that fits your livery. Every download is counted.
            </FeatureCard>
          </div>
        </Section>
      </Anchor>

      {/* Tips */}
      <Anchor id="tips">
        <Section
          label="/ Pro tips"
          title="Get the best in-game result, every time"
        >
          <ul className="divide-y divide-[var(--border)] border border-[var(--border)] bg-white">
            {[
              ["Crop tight in your image editor first", "Trim excess empty space before upload. Our auto-crop catches most of it, but a tight source = tighter viewBox = bigger in-game decal."],
              ["Start in B/W for logos", "Line-art and most sponsor logos don't need colour. B/W almost always fits under 15 KB with no compromise."],
              ["Pick High quality + split if detail matters", "For intricate, multi-colour logos, use High quality then immediately split into 2×2 pieces — reassemble in the livery editor for a decal much larger than any single 15 KB SVG could deliver."],
              ["Use transparent PNGs whenever possible", "They skip the bg-detect step and give the cleanest trace."],
              ["If colours look flat in-game", "Switch to High quality and/or boost Colors to 6–8. The tracer preserves saturated hues thanks to a saturation-weighted palette selector."],
              ["Test with a cheap car first", "Import the SVG as a small decal, scale it up in-game — this is the fastest way to verify alignment and colour fidelity."],
            ].map(([t, d], i) => (
              <li key={i} className="p-5 grid grid-cols-[28px_1fr] gap-4 items-start">
                <CheckCircle size={20} weight="fill" className="text-[var(--success)] mt-0.5" />
                <div>
                  <div className="font-display font-bold">{t}</div>
                  <div className="text-sm text-[var(--fg-2)] mt-1">{d}</div>
                </div>
              </li>
            ))}
          </ul>
        </Section>
      </Anchor>

      {/* Troubleshooting */}
      <Anchor id="trouble">
        <Section label="/ Troubleshooting" title="Common issues & quick fixes">
          <ul className="divide-y divide-[var(--border)] border border-[var(--border)] bg-white">
            {[
              ["Output looks blank / missing content", "Your source probably has a flat background. Click Make background transparent or try B/W mode. For low-contrast content, bump Threshold up."],
              ["Decal appears tiny in-game", "Crop your image tighter and re-run — our auto-crop + 1024 px normalization then produce a decal that fills the livery slot."],
              ["File exceeds 15 KB", "Drop Colors to 2–3, raise Simplification, OR use Split Pieces for a bigger, crisper combined decal."],
              ["Colours look faded", "Use High quality and keep Colors ≤ 6. Our saturation-weighted palette preserves vivid hues but dense gradients still soft-average."],
              ["The game rejects the SVG on import", "Paste it into the Repair page — it strips filters, masks, text and other unsupported elements, and returns a validated copy."],
              ["Split tiles don't line up in-game", "Import tiles in the exact grid order they were generated (R0C0 top-left → RnCn bottom-right). The livery editor snaps them precisely if you zero the offsets."],
            ].map(([t, d], i) => (
              <li key={i} className="p-5 grid grid-cols-[28px_1fr] gap-4 items-start">
                <Warning size={20} weight="fill" className="text-[var(--warning)] mt-0.5" />
                <div>
                  <div className="font-display font-bold">{t}</div>
                  <div className="text-sm text-[var(--fg-2)] mt-1">{d}</div>
                </div>
              </li>
            ))}
          </ul>
        </Section>
      </Anchor>

      {/* CTA */}
      <section className="border-t border-[var(--border)]">
        <div className="px-6 md:px-10 py-20 md:py-24 text-center">
          <div className="micro-label">/ Ready</div>
          <h2 className="font-display font-black text-4xl md:text-5xl mt-2 max-w-3xl mx-auto">
            You've got the playbook. Put it on the grid.
          </h2>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/convert" className="btn-accent inline-flex items-center gap-2">
              Open the converter <ArrowRight size={14} weight="bold" />
            </Link>
            <Link to="/showcase" className="btn-ghost">Browse Showcase</Link>
          </div>
        </div>
      </section>
    </div>
  );
}

// ---------- building blocks ----------
function Anchor({ id, children }) {
  return (
    <div id={id} className="scroll-mt-20">
      {children}
    </div>
  );
}

function Section({ label, title, intro, children }) {
  return (
    <section className="border-b border-[var(--border)]">
      <div className="px-6 md:px-10 py-14 md:py-16">
        <div className="mb-8">
          <div className="micro-label">{label}</div>
          <h2 className="font-display font-black text-3xl md:text-4xl tracking-tight mt-1">
            {title}
          </h2>
          {intro && (
            <p className="mt-4 text-[var(--fg-2)] max-w-3xl">{intro}</p>
          )}
        </div>
        {children}
      </div>
    </section>
  );
}

function Step({ n, icon, title, children }) {
  return (
    <div className="bg-white p-6 md:p-8">
      <div className="flex items-center justify-between">
        <span className="font-display font-black text-3xl text-[var(--accent)]">{n}</span>
        <div className="h-10 w-10 border border-black flex items-center justify-center">
          {icon}
        </div>
      </div>
      <div className="font-display font-bold text-xl mt-6">{title}</div>
      <div className="text-sm text-[var(--fg-2)] mt-2 leading-relaxed">{children}</div>
    </div>
  );
}

function ModeCard({ icon, name, caption, children }) {
  return (
    <div className="bg-white p-6 md:p-8">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 border border-black flex items-center justify-center">
          {icon}
        </div>
        <div>
          <div className="font-display font-bold text-xl">{name}</div>
          <div className="text-xs font-mono text-[var(--fg-2)] tracking-wider uppercase">
            {caption}
          </div>
        </div>
      </div>
      <div className="mt-4 text-sm leading-relaxed text-[var(--fg-2)]">{children}</div>
    </div>
  );
}

function QualityCard({ icon, name, meta, children }) {
  return (
    <div className="bg-white p-6 md:p-8">
      <div className="flex items-center justify-between">
        <div className="h-10 w-10 border border-black flex items-center justify-center">
          {icon}
        </div>
        <span className="chip">{meta}</span>
      </div>
      <div className="font-display font-bold text-xl mt-5">{name}</div>
      <div className="text-sm text-[var(--fg-2)] mt-2 leading-relaxed">{children}</div>
    </div>
  );
}

function Control({ label, range, children }) {
  return (
    <div className="bg-white p-6 md:p-8">
      <div className="flex items-center justify-between">
        <div className="font-display font-bold text-lg">{label}</div>
        <span className="chip font-mono">{range}</span>
      </div>
      <div className="mt-3 text-sm text-[var(--fg-2)] leading-relaxed">{children}</div>
    </div>
  );
}

function FeatureCard({ icon, title, children }) {
  return (
    <div className="bg-white p-6 md:p-8">
      <div className="h-10 w-10 border border-black flex items-center justify-center">
        {icon}
      </div>
      <div className="font-display font-bold text-lg mt-5">{title}</div>
      <div className="text-sm text-[var(--fg-2)] mt-2 leading-relaxed">{children}</div>
    </div>
  );
}

function Note({ tone = "info", children }) {
  const toneMap = {
    info: { bg: "#F0F4FF", border: "#4A90E2", ico: "#4A90E2" },
    success: { bg: "#EEFBF2", border: "#00C853", ico: "#00C853" },
  };
  const t = toneMap[tone] || toneMap.info;
  return (
    <div
      className="mt-6 p-4 text-sm leading-relaxed border"
      style={{ background: t.bg, borderColor: t.border }}
    >
      <span className="status-dot" style={{ background: t.ico }} />
      {children}
    </div>
  );
}
